import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Avatar, AvatarFallback } from './ui/avatar';
import { 
  LayoutDashboard, 
  FileText, 
  Users, 
  CheckSquare, 
  BarChart3,
  Settings, 
  LogOut,
  Menu,
  X,
  Archive
} from 'lucide-react';

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navigation = [
    { name: 'Dashboard', path: '/', icon: LayoutDashboard, testId: 'nav-dashboard' },
    { name: 'My Appraisals', path: '/my-appraisals', icon: FileText, testId: 'nav-my-appraisals' },
    { name: 'Archived Appraisals', path: '/archived', icon: Archive, testId: 'nav-archived' },
  ];

  if (user?.role === 'MANAGER' || user?.role === 'HR_ADMIN') {
    navigation.push(
      { name: 'Team Appraisals', path: '/team-appraisals', icon: Users, testId: 'nav-team-appraisals' },
      { name: '2nd-Level Reviews', path: '/second-level', icon: CheckSquare, testId: 'nav-second-level' },
      { name: 'Analytics', path: '/analytics', icon: BarChart3, testId: 'nav-analytics' }
    );
  }

  if (user?.role === 'HR_ADMIN') {
    navigation.push({ name: 'Admin', path: '/admin', icon: Settings, testId: 'nav-admin' });
  }

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getRoleLabel = (role) => {
    const labels = {
      'EMPLOYEE': 'Employee',
      'MANAGER': 'Manager',
      'HR_ADMIN': 'HR Admin'
    };
    return labels[role] || role;
  };

  return (
    <div className="min-h-screen flex bg-[#F8FAFC]">
      {/* Sidebar */}
      <aside className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200 transform transition-transform duration-200 ease-in-out
        lg:relative lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `} data-testid="sidebar">
        <div className="h-full flex flex-col">
          {/* Logo */}
          <div className="p-6 border-b border-slate-200">
            <h1 className="text-2xl font-semibold text-slate-900 tracking-tight" data-testid="app-title">Mestastop</h1>
            <p className="text-xs text-slate-500 mt-1">Performance Appraisal</p>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1" data-testid="sidebar-nav">
            {navigation.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-sm text-sm transition-colors duration-200
                    ${
                      isActive
                        ? 'bg-[#1E40AF] text-white'
                        : 'text-slate-700 hover:bg-slate-50'
                    }
                  `}
                  data-testid={item.testId}
                >
                  <Icon className="h-5 w-5" />
                  {item.name}
                </Link>
              );
            })}
          </nav>

          {/* User Profile */}
          <div className="p-4 border-t border-slate-200">
            <div className="flex items-center gap-3 mb-3">
              <Avatar className="h-10 w-10 border border-slate-200" data-testid="user-avatar">
                <AvatarFallback className="bg-[#1E40AF] text-white text-sm font-semibold">
                  {getInitials(user?.name || 'U')}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-slate-900 truncate" data-testid="user-name">{user?.name}</div>
                <div className="text-xs text-slate-500" data-testid="user-role">{getRoleLabel(user?.role)}</div>
              </div>
            </div>
            <div className="space-y-2">
              <Link
                to="/profile"
                onClick={() => setSidebarOpen(false)}
                className="w-full flex items-center justify-start px-4 py-2 border border-slate-200 rounded-sm text-sm text-slate-700 hover:bg-slate-50 transition-colors duration-200"
                data-testid="profile-link"
              >
                <Settings className="mr-2 h-4 w-4" />
                Profile Settings
              </Link>
              <Button
                onClick={handleLogout}
                variant="outline"
                className="w-full justify-start border-slate-200 rounded-sm text-sm"
                data-testid="logout-button"
              >
                <LogOut className="mr-2 h-4 w-4" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Bar (Mobile) */}
        <header className="lg:hidden bg-white border-b border-slate-200 p-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="rounded-sm"
            data-testid="mobile-menu-button"
          >
            {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-8" data-testid="main-content">
          <div className="max-w-7xl mx-auto">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
